import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ChevronDown, Search, Copy, CheckCircle, Info, Upload, X, Loader2 } from 'lucide-react';
import { QRCode } from 'react-qrcode-logo';
import * as Popover from '@radix-ui/react-popover';
import { useScreenSize } from '@/hooks';
import { useToast } from '@/hooks/useToast';
import Button from '@/components/common/Button/Button';
import coinsData from '@/utils/coins';
import socket from '@/utils/socketInstance';
import { 
  C, encode, decode, forceSatoshiFormat, 
  storage, Event, __, wait, getUID, API 
} from '@/utils';
import {
  fetchCoinDetails,
  createCryptoDeposit,
  fetchBankDetails,
  createINRPayment,
  createOkPayPayment,
  createManualDeposit,
  createCricPayPayment
} from '@/services/deposit/depositService';
import { searchGiftCard, activateGiftCard } from '@/services/giftCard/giftCardService';
import GiftCardViewModal from '@/components/giftCards/GiftCardViewModal';
import styles from './DepositTab.module.css';

/**
 * DepositTab Component
 * Full deposit functionality with crypto and fiat support
 * Uses React Query for API calls and socket for real-time updates
 */
const DepositTab = () => {
  const { isMobile } = useScreenSize();
  const { showSuccess, showError, showInfo } = useToast();
  
  // State
  const [selectedCoin, setSelectedCoin] = useState(storage.getKey('coin') || 'INR');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [amount, setAmount] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCoinDropdownOpen, setIsCoinDropdownOpen] = useState(false);
  const [isNetworkDropdownOpen, setIsNetworkDropdownOpen] = useState(false);
  const [isBankDropdownOpen, setIsBankDropdownOpen] = useState(false);
  const [depositAddress, setDepositAddress] = useState('');
  const [qrString, setQrString] = useState('');
  const [showDepositDetails, setShowDepositDetails] = useState(false);
  const [copiedStates, setCopiedStates] = useState({});
  const [paymentMode, setPaymentMode] = useState('automatic'); // 'automatic' | 'manual'
  const [upiMethod, setUpiMethod] = useState('upi1'); // 'upi1' | 'upi2' | 'upi3' | 'netbanking'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [credits, setCredits] = useState({});
  const [activeCoins, setActiveCoins] = useState(
    localStorage.getItem('active_coins')?.split(',').map(String) || ['1', '2', '3', '4']
  );
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardValid, setGiftCardValid] = useState(false);
  const [giftCardError, setGiftCardError] = useState('');
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [giftCardDetails, setGiftCardDetails] = useState(null);
  const [giftCardSearchResults, setGiftCardSearchResults] = useState(null);
  const [isSearchingGiftCard, setIsSearchingGiftCard] = useState(false);
  const [isGiftCardPopoverOpen, setIsGiftCardPopoverOpen] = useState(false);
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [selectedGiftCard, setSelectedGiftCard] = useState(null);
  const giftCardSearchTimerRef = useRef(null);

  // Fiat currencies configuration
  const FIAT_CURRENCIES = {
    INR: { payType: 'UPI', automatic: true, manual: true, minAmount: 100 },
    MVR: { payType: 'Bank', automatic: false, manual: true, minAmount: 100 },
    PKR: { payType: 'Bank', automatic: false, manual: true, minAmount: 100 },
    AED: { payType: 'Bank', automatic: false, manual: true, minAmount: 100 },
    BDT: { payType: 'Bank', automatic: false, manual: true, minAmount: 100 }
  };

  const isFiatCurrency = (currency) => Object.keys(FIAT_CURRENCIES).includes(currency);

  // Filter coins
  const filteredCoins = coinsData
    .filter(coin => {
      if (!activeCoins.includes(coin.id.toString())) return false;
      if (coin.preffix === 'BJT' || coin.preffix === 'SC') return false;
      return coin.preffix.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      if (a.preffix === 'BTC') return -1;
      if (b.preffix === 'BTC') return 1;
      return 0;
    });

  const selectedCoinData = coinsData.find(coin => coin.preffix === selectedCoin);

  // Fetch coin details (for crypto)
  const { data: coinDetails, isLoading: isLoadingCoinDetails } = useQuery({
    queryKey: ['coinDetails', selectedCoin],
    queryFn: () => fetchCoinDetails(selectedCoin),
    enabled: !isFiatCurrency(selectedCoin) && selectedCoin !== 'INR',
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Fetch bank details (for fiat)
  const { data: bankDetailsList = [], isLoading: isLoadingBanks } = useQuery({
    queryKey: ['bankDetails', selectedCoin],
    queryFn: () => fetchBankDetails(selectedCoin),
    enabled: isFiatCurrency(selectedCoin),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Set default network when coin details load
  useEffect(() => {
    if (coinDetails?.networks) {
      const networks = Object.keys(coinDetails.networks);
      if (networks.length > 0 && !selectedNetwork) {
        setSelectedNetwork(networks[0]);
      }
    }
  }, [coinDetails, selectedNetwork]);

  // Set default bank when bank details load
  useEffect(() => {
    if (bankDetailsList.length > 0 && !selectedBank) {
      setSelectedBank(bankDetailsList[0].bank_name);
    }
  }, [bankDetailsList, selectedBank]);

  // Socket: Listen for credit updates
  useEffect(() => {
    const token = storage.getKey('token');
    if (!token || !socket) return;

    // Request initial credit
    socket.emit(C.CREDIT, encode({ token, coin: selectedCoin }));

    const handleCredit = (data) => {
      const decoded = decode(data);
      if (decoded?.credit) {
        setCredits(decoded.credit);
        storage.setKey('credits', decoded.credit);
      }
    };

    const handleCreditUpdate = (data) => {
      const decoded = decode(data);
      if (decoded?.value && decoded?.coin) {
        setCredits(prev => ({
          ...prev,
          [__.lowerCase(decoded.coin)]: decoded.value
        }));
        if (__.lowerCase(decoded.coin) === __.lowerCase(selectedCoin)) {
          const formatted = forceSatoshiFormat(decoded.value, selectedCoin);
          storage.setKey('credit', formatted);
        }
      }
    };

    socket.on(C.CREDIT, handleCredit);
    socket.on(C.UPDATE_CREDIT, handleCreditUpdate);

    return () => {
      socket.off(C.CREDIT, handleCredit);
      socket.off(C.UPDATE_CREDIT, handleCreditUpdate);
    };
  }, [selectedCoin]);

  // Listen for coin_changed event
  useEffect(() => {
    const handleCoinChanged = (coinName) => {
      setSelectedCoin(coinName);
      setSelectedNetwork('');
      setShowDepositDetails(false);
      setDepositAddress('');
      setQrString('');
    };

    Event.on('coin_changed', handleCoinChanged);
    return () => Event.off('coin_changed', handleCoinChanged);
  }, []);

  // Reset form when coin changes
  useEffect(() => {
    setAmount('');
    setShowDepositDetails(false);
    setDepositAddress('');
    setQrString('');
    setShowTransactionForm(false);
    setTransactionId('');
    setScreenshot(null);
    setPhoneNumber('');
    setShowIframe(false);
    setIframeUrl('');
    setGiftCardCode('');
    setGiftCardValid(false);
    setGiftCardError('');
    setGiftCardDetails(null);
    setGiftCardSearchResults(null);
    setIsGiftCardPopoverOpen(false);
    setShowGiftCardModal(false);
    setSelectedGiftCard(null);
    // Clear search timer
    if (giftCardSearchTimerRef.current) {
      clearTimeout(giftCardSearchTimerRef.current);
    }
  }, [selectedCoin]);

  // Crypto deposit mutation
  const cryptoDepositMutation = useMutation({
    mutationFn: createCryptoDeposit,
    onSuccess: (data) => {
      if (data?.data?.address) {
        setDepositAddress(data.data.address);
        setQrString(data.data.address);
        setShowDepositDetails(true);
        showSuccess('Deposit address generated successfully');
      } else {
        showError('Failed to generate deposit address');
      }
    },
    onError: (error) => {
      showError(error.message || 'Failed to create deposit');
    }
  });

  // INR payment mutations
  const inrPaymentMutation = useMutation({
    mutationFn: createINRPayment,
    onSuccess: (data) => {
      if (data.success && data.url) {
        setIframeUrl(data.url);
        setShowIframe(true);
      } else {
        showError('Payment initiation failed');
      }
    },
    onError: (error) => {
      showError(error.message || 'Payment initiation failed');
    }
  });

  const okPayMutation = useMutation({
    mutationFn: createOkPayPayment,
    onSuccess: (data) => {
      if (data.code === 0 && data.data?.url) {
        setIframeUrl(data.data.url);
        setShowIframe(true);
      } else {
        showError('Payment initiation failed');
      }
    },
    onError: (error) => {
      showError(error.message || 'Payment initiation failed');
    }
  });

  const cricPayMutation = useMutation({
    mutationFn: createCricPayPayment,
    onSuccess: (data) => {
      if (data.status === 'Success' && data.accessURL) {
        setIframeUrl(data.accessURL);
        setShowIframe(true);
      } else {
        showError('Payment initiation failed');
      }
    },
    onError: (error) => {
      showError(error.message || 'Payment initiation failed');
    }
  });

  // Manual deposit mutation
  const manualDepositMutation = useMutation({
    mutationFn: createManualDeposit,
    onSuccess: (data) => {
      if (data.success) {
        showSuccess('Deposit request submitted successfully');
        setShowTransactionForm(false);
        setTransactionId('');
        setScreenshot(null);
        setAmount('');
        // Refresh credits
        const token = storage.getKey('token');
        if (token && socket) {
          socket.emit(C.CREDIT, encode({ token, coin: selectedCoin }));
        }
      } else {
        showError('Failed to submit deposit request');
      }
    },
    onError: (error) => {
      showError(error.message || 'Failed to submit deposit request');
    }
  });

  // Handle coin selection
  const handleCoinSelect = (coinName) => {
    // Update state FIRST for instant UI feedback - always update even if already selected
    setSelectedCoin(coinName);
    
    // If already selected, just ensure state is synced and return
    const currentCoin = storage.getKey('coin');
    if (currentCoin === coinName) {
      setIsCoinDropdownOpen(false);
      return;
    }

    // Update storage
    storage.setKey('coin', coinName);
    setSelectedNetwork('');
    setShowDepositDetails(false);
    setIsCoinDropdownOpen(false);
    
    // Update credit for selected coin
    const coinKey = __.lowerCase(coinName);
    const creditVal = credits[coinKey] || 0;
    const formattedCredit = forceSatoshiFormat(creditVal, coinName);
    storage.setKey('credit', formattedCredit);
    
    // Emit coin_changed event
    wait(200).then(() => {
      Event.emit('coin_changed', coinName);
    });

    // Request updated credit
    const token = storage.getKey('token');
    if (token && socket) {
      socket.emit(C.CREDIT, encode({ token, coin: coinName }));
    }
  };

  // Handle copy
  const handleCopy = (field, value) => {
    navigator.clipboard.writeText(value);
    setCopiedStates(prev => ({ ...prev, [field]: true }));
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [field]: false }));
    }, 2000);
    showSuccess('Copied to clipboard');
  };

  // Handle crypto deposit
  const handleCryptoDeposit = () => {
    if (!amount || parseFloat(amount) <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    if (!selectedNetwork) {
      showError('Please select a network');
      return;
    }

    if (!coinDetails?.coinId) {
      showError('Coin details not loaded. Please try again.');
      return;
    }

    const depositData = {
      coinId: coinDetails.coinId,
      price: amount,
      orderId: `order${Date.now()}`,
      chain: selectedNetwork,
      generateCheckoutURL: true,
      returnUrl: 'https://jackopot.com/',
    };

    // Add gift card code if validated
    if (giftCardValid && giftCardCode) {
      depositData.giftCardCode = giftCardCode;
    }

    cryptoDepositMutation.mutate(depositData);
  };

  // Handle INR automatic payment
  const handleINRAutomaticPayment = () => {
    const minAmount = FIAT_CURRENCIES.INR.minAmount;
    if (!amount || parseFloat(amount) < minAmount) {
      showError(`Minimum deposit amount is ₹${minAmount}`);
      return;
    }

    const paymentData = {
      amount: amount,
      giftCardCode: giftCardValid && giftCardCode ? giftCardCode : undefined
    };

    if (upiMethod === 'upi1') {
      inrPaymentMutation.mutate({
        ...paymentData,
        paymentSystem: 'phonepe'
      });
    } else if (upiMethod === 'upi2') {
      if (!phoneNumber || phoneNumber.length < 10) {
        showError('Please enter a valid phone number');
        return;
      }
      okPayMutation.mutate({
        ...paymentData,
        phoneNumber: phoneNumber
      });
    } else if (upiMethod === 'upi3') {
      if (parseFloat(amount) < 1000) {
        showError('Minimum amount for this method is ₹1000');
        return;
      }
      cricPayMutation.mutate({
        ...paymentData,
        paymentMethod: 'PS002'
      });
    } else if (upiMethod === 'netbanking') {
      if (parseFloat(amount) < 1000) {
        showError('Minimum amount for this method is ₹1000');
        return;
      }
      cricPayMutation.mutate({
        ...paymentData,
        paymentMethod: 'PS003'
      });
    }
  };

  // Handle manual deposit submission
  const handleManualDepositSubmit = () => {
    if (!transactionId || !screenshot) {
      showError('Please fill all required fields');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    const formData = new FormData();
    formData.append('user_id', getUID());
    formData.append('amount', amount);
    formData.append('transaction_id', transactionId);
    formData.append('screenshot', screenshot);
    formData.append('currency', selectedCoin);
    
    // Add gift card code if validated
    if (giftCardValid && giftCardCode) {
      formData.append('gift_card_code', giftCardCode);
    }

    const selectedBankData = bankDetailsList.find(b => b.bank_name === selectedBank);
    if (selectedBankData) {
      formData.append('bank_name', selectedBank);
      formData.append('account_number', selectedBankData.account_number);
      formData.append('ifsc_code', selectedBankData.ifsc_code);
      formData.append('account_holder_name', selectedBankData.account_holder_name);
    }

    manualDepositMutation.mutate(formData);
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setScreenshot(file);
    }
  };

  // Search gift card with debouncing (similar to GiftCards.jsx)
  useEffect(() => {
    if (giftCardSearchTimerRef.current) {
      clearTimeout(giftCardSearchTimerRef.current);
    }

    const trimmedCode = giftCardCode.trim();

    // If empty, close popover and clear results
    if (!trimmedCode) {
      setGiftCardSearchResults(null);
      setIsGiftCardPopoverOpen(false);
      setIsSearchingGiftCard(false);
      setGiftCardValid(false);
      setGiftCardDetails(null);
      setGiftCardError('');
      return;
    }

    // Only trigger API call if at least 3 characters
    if (trimmedCode.length < 3) {
      setGiftCardSearchResults(null);
      setIsSearchingGiftCard(false);
      // Keep popover open if user is typing (1-2 chars) to show message
      if (trimmedCode.length > 0) {
        setIsGiftCardPopoverOpen(true);
      } else {
        setIsGiftCardPopoverOpen(false);
      }
      return;
    }

    // Open popover and start searching
    setIsSearchingGiftCard(true);
    setIsGiftCardPopoverOpen(true);

    giftCardSearchTimerRef.current = setTimeout(async () => {
      try {
        const response = await searchGiftCard(trimmedCode);
        if (response.success && response.giftCard) {
          // Transform API response to match component structure
          const transformedCard = {
            cardId: response.giftCard.id.toString(),
            title: response.giftCard.description || `Gift Card ${response.giftCard.uniqueKey}`,
            amount: response.giftCard.amount || 0,
            staticValue: response.giftCard.amount || 0,
            wagerRequirement: response.giftCard.wagerStatus ? response.giftCard.wagerTimes : 0,
            status: 'unclaimed',
            description: response.giftCard.description || '',
            expiryDate: response.giftCard.endDate || null,
            activationCode: response.giftCard.uniqueKey,
            requiresDeposit: response.giftCard.depositStatus || false,
            depositAmount: response.giftCard.depositAmount || 0,
            currentWager: 0,
            totalWager: (response.giftCard.amount || 0) * (response.giftCard.wagerTimes || 0),
            isActivated: false,
            activatedDate: null,
            // API specific fields
            periodDays: response.giftCard.periodDays,
            allUserStatus: response.giftCard.allUserStatus,
            isActive: response.giftCard.isActive,
            createdAt: response.giftCard.createdAt,
            updatedAt: response.giftCard.updatedAt,
          };
          setGiftCardSearchResults(transformedCard);
          setGiftCardValid(true);
          setGiftCardDetails(transformedCard);
          setGiftCardError('');
        } else {
          setGiftCardSearchResults(null);
          setGiftCardValid(false);
          setGiftCardDetails(null);
        }
      } catch (error) {
        console.error('Error searching gift card:', error);
        setGiftCardSearchResults(null);
        setGiftCardValid(false);
        setGiftCardDetails(null);
        // Error is handled silently in search - user will see "No gift card found"
      } finally {
        setIsSearchingGiftCard(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (giftCardSearchTimerRef.current) {
        clearTimeout(giftCardSearchTimerRef.current);
      }
    };
  }, [giftCardCode]);

  // Handle clicking on gift card in search results to view details
  const handleViewGiftCardDetails = () => {
    if (giftCardSearchResults) {
      setSelectedGiftCard(giftCardSearchResults);
      setShowGiftCardModal(true);
      setIsGiftCardPopoverOpen(false);
    }
  };

  // Handle gift card activation from modal
  const handleGiftCardActivate = async (code) => {
    // After activation, the gift card is validated and code is set
    const activationCode = code || selectedGiftCard?.activationCode || giftCardCode;
    setGiftCardValid(true);
    setGiftCardCode(activationCode);
    setGiftCardDetails(selectedGiftCard || giftCardSearchResults);
    showSuccess('Gift card activated! It will be applied with your deposit.');
    setShowGiftCardModal(false);
    setSelectedGiftCard(null);
    setIsGiftCardPopoverOpen(false);
  };

  // Handle gift card code change
  const handleGiftCardCodeChange = (e) => {
    const value = e.target.value;
    setGiftCardCode(value);
    // Reset validation state when code changes
    if (giftCardValid && value !== giftCardDetails?.activationCode) {
      setGiftCardValid(false);
      setGiftCardDetails(null);
    }
    setGiftCardError('');
    
    // Open popover if user has typed at least 3 characters
    if (value.trim().length >= 3) {
      setIsGiftCardPopoverOpen(true);
    } else if (value.trim().length === 0) {
      setIsGiftCardPopoverOpen(false);
    }
  };

  // Get selected bank details
  const selectedBankDetails = bankDetailsList.find(b => b.bank_name === selectedBank);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.dropdownContainer}`)) {
        setIsCoinDropdownOpen(false);
        setIsNetworkDropdownOpen(false);
        setIsBankDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const isLoading = cryptoDepositMutation.isPending || 
                   inrPaymentMutation.isPending || 
                   okPayMutation.isPending || 
                   cricPayMutation.isPending ||
                   isLoadingCoinDetails ||
                   isLoadingBanks;

  // Get all active coins for quick select (prioritize popular ones, then show all)
  const popularCoins = ['BTC', 'ETH', 'USDT', 'DOGE', 'BNB', 'INR', 'LTC', 'ADA', 'XRP'];
  const quickSelectCoins = coinsData
    .filter(coin => {
      if (!activeCoins.includes(coin.id.toString())) return false;
      if (coin.preffix === 'BJT' || coin.preffix === 'SC' || coin.preffix === 'JPC') return false;
      return true;
    })
    .sort((a, b) => {
      const aIndex = popularCoins.indexOf(a.preffix);
      const bIndex = popularCoins.indexOf(b.preffix);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.preffix.localeCompare(b.preffix);
    })
    .slice(0, 8); // Show max 8 coins in quick select

  // Helper function to get coin image path
  // Maps coin prefixes to actual image filenames in /assets/images/coins/ directory
  const getCoinImagePath = (coin) => {
    if (!coin || !coin.image) return '/assets/images/coins/BTC.webp';
    
    // Map coin prefixes to actual image filenames in coins directory
    // This ensures we use the correct filenames that exist in /public/assets/images/coins/
    const coinImageMap = {
      'USDT': 'USDT.webp', // Primary: USDT.webp, fallback: TETHER.webp (handled in onError)
      'TRX': 'TRON.webp',  // TRON.webp in directory
    };
    
    // Use mapped image if available, otherwise use coin.image
    let imageName = coinImageMap[coin.preffix] || coin.image;
    
    // Remove any existing path prefixes
    imageName = imageName.replace(/^coins\//, '').replace(/^assets\/images\//, '');
    
    // Always use coins directory
    return `/assets/images/coins/${imageName}`;
  };

  return (
    <div className={styles.depositTab}>
      {/* Token Quick Select */}
      <div className={styles.tokenList}>
        {quickSelectCoins.map(coin => {
          return (
            <button
              key={coin.id}
              className={`${styles.tokenButton} ${selectedCoin === coin.preffix ? styles.tokenButtonActive : ''}`}
              onClick={() => handleCoinSelect(coin.preffix)}
            >
              <img 
                src={getCoinImagePath(coin)}
                alt={coin.preffix}
                className={styles.tokenIcon}
                onError={(e) => {
                  // Fallback: try TETHER.webp for USDT, or BTC.webp
                  if (coin.preffix === 'USDT' && e.target.src.includes('USDT.webp')) {
                    e.target.src = '/assets/images/coins/TETHER.webp';
                  } else {
                    e.target.src = '/assets/images/coins/BTC.webp';
                  }
                }}
              />
              <span className={styles.tokenName}>{coin.preffix}</span>
            </button>
          );
        })}
      </div>

      {/* Coin Selection */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Deposit Currency</label>
        <div className={`${styles.dropdownContainer} ${styles.dropdownContainer}`}>
          <button
            className={styles.dropdownButton}
            onClick={(e) => {
              e.stopPropagation();
              setIsCoinDropdownOpen(!isCoinDropdownOpen);
            }}
          >
            <div className={styles.dropdownButtonContent}>
              {selectedCoinData && (
                <img
                  src={getCoinImagePath(selectedCoinData)}
                  alt={selectedCoin}
                  className={styles.coinImage}
                  onError={(e) => {
                    // Fallback: try TETHER.webp for USDT, or BTC.webp
                    if (selectedCoin === 'USDT' && e.target.src.includes('USDT.webp')) {
                      e.target.src = '/assets/images/coins/TETHER.webp';
                    } else {
                      e.target.src = '/assets/images/coins/BTC.webp';
                    }
                  }}
                />
              )}
              <span className={styles.coinName}>{selectedCoin}</span>
            </div>
            <ChevronDown size={20} className={styles.chevron} />
          </button>

          {isCoinDropdownOpen && (
            <div className={styles.dropdownPanel} onClick={(e) => e.stopPropagation()}>
              <div className={styles.searchContainer}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search coins..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>

              <div className={styles.coinList}>
                {filteredCoins.map((coin) => (
                  <div
                    key={coin.id}
                    className={`${styles.coinOption} ${selectedCoin === coin.preffix ? styles.coinOptionSelected : ''}`}
                    onClick={() => handleCoinSelect(coin.preffix)}
                  >
                    <img
                      src={getCoinImagePath(coin)}
                      alt={coin.preffix}
                      className={styles.coinImage}
                      onError={(e) => {
                        // Fallback: try TETHER.webp for USDT, or BTC.webp
                        if (coin.preffix === 'USDT' && e.target.src.includes('USDT.webp')) {
                          e.target.src = '/assets/images/coins/TETHER.webp';
                        } else {
                          e.target.src = '/assets/images/coins/BTC.webp';
                        }
                      }}
                    />
                    <span className={styles.coinName}>{coin.preffix}</span>
                    <span className={styles.coinBalance}>
                      {forceSatoshiFormat(credits[__.lowerCase(coin.preffix)] || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Network/Bank Selection */}
      {!isFiatCurrency(selectedCoin) && coinDetails?.networks && (
        <div className={styles.formGroup}>
          <label className={styles.label}>Choose Network</label>
          <div className={`${styles.dropdownContainer} ${styles.dropdownContainer}`}>
            <button
              className={styles.dropdownButton}
              onClick={(e) => {
                e.stopPropagation();
                setIsNetworkDropdownOpen(!isNetworkDropdownOpen);
              }}
            >
              <span className={styles.coinName}>{selectedNetwork || 'Select Network'}</span>
              <ChevronDown size={20} className={styles.chevron} />
            </button>

            {isNetworkDropdownOpen && (
              <div className={styles.dropdownPanel} onClick={(e) => e.stopPropagation()}>
                {Object.keys(coinDetails.networks).map((network) => (
                  <div
                    key={network}
                    className={`${styles.coinOption} ${selectedNetwork === network ? styles.coinOptionSelected : ''}`}
                    onClick={() => {
                      setSelectedNetwork(network);
                      setIsNetworkDropdownOpen(false);
                    }}
                  >
                    <span className={styles.coinName}>{network}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isFiatCurrency(selectedCoin) && bankDetailsList.length > 0 && (
        <div className={styles.formGroup}>
          <label className={styles.label}>Choose Bank</label>
          <div className={`${styles.dropdownContainer} ${styles.dropdownContainer}`}>
            <button
              className={styles.dropdownButton}
              onClick={(e) => {
                e.stopPropagation();
                setIsBankDropdownOpen(!isBankDropdownOpen);
              }}
            >
              <span className={styles.coinName}>{selectedBank || 'Select Bank'}</span>
              <ChevronDown size={20} className={styles.chevron} />
            </button>

            {isBankDropdownOpen && (
              <div className={styles.dropdownPanel} onClick={(e) => e.stopPropagation()}>
                {bankDetailsList.map((bank) => (
                  <div
                    key={bank.id || bank.bank_name}
                    className={`${styles.coinOption} ${selectedBank === bank.bank_name ? styles.coinOptionSelected : ''}`}
                    onClick={() => {
                      setSelectedBank(bank.bank_name);
                      setIsBankDropdownOpen(false);
                    }}
                  >
                    <span className={styles.coinName}>{bank.bank_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deposit Details (QR Code & Address) */}
      {showDepositDetails && depositAddress && (
        <div className={styles.depositDetails}>
          <div className={styles.qrCodeContainer}>
            <QRCode value={qrString} size={200} />
          </div>
          <div className={styles.addressContainer}>
            <label className={styles.label}>Deposit Address</label>
            <div className={styles.addressBox}>
              <span className={styles.addressText}>{depositAddress}</span>
              <button
                className={styles.copyButton}
                onClick={() => handleCopy('address', depositAddress)}
              >
                {copiedStates.address ? (
                  <CheckCircle size={18} />
                ) : (
                  <Copy size={18} />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Details (for fiat) */}
      {isFiatCurrency(selectedCoin) && selectedBankDetails && paymentMode === 'manual' && (
        <div className={styles.bankDetails}>
          {selectedBankDetails.qr_image && (
            <div className={styles.qrCodeContainer}>
              <img src={selectedBankDetails.qr_image} alt="QR Code" />
            </div>
          )}
          <div className={styles.bankInfo}>
            {selectedBankDetails.account_holder_name && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Account Holder:</span>
                <div className={styles.infoValue}>
                  <span>{selectedBankDetails.account_holder_name}</span>
                  <button
                    className={styles.copyButton}
                    onClick={() => handleCopy('accountName', selectedBankDetails.account_holder_name)}
                  >
                    {copiedStates.accountName ? <CheckCircle size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            )}
            {selectedBankDetails.account_number && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Account Number:</span>
                <div className={styles.infoValue}>
                  <span>{selectedBankDetails.account_number}</span>
                  <button
                    className={styles.copyButton}
                    onClick={() => handleCopy('accountNumber', selectedBankDetails.account_number)}
                  >
                    {copiedStates.accountNumber ? <CheckCircle size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            )}
            {selectedBankDetails.ifsc_code && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>IFSC Code:</span>
                <div className={styles.infoValue}>
                  <span>{selectedBankDetails.ifsc_code}</span>
                  <button
                    className={styles.copyButton}
                    onClick={() => handleCopy('ifscCode', selectedBankDetails.ifsc_code)}
                  >
                    {copiedStates.ifscCode ? <CheckCircle size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            )}
            {selectedBankDetails.upi_id && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>UPI ID:</span>
                <div className={styles.infoValue}>
                  <span>{selectedBankDetails.upi_id}</span>
                  <button
                    className={styles.copyButton}
                    onClick={() => handleCopy('upiId', selectedBankDetails.upi_id)}
                  >
                    {copiedStates.upiId ? <CheckCircle size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Amount Input */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          Enter Deposit Amount
          {selectedCoin === 'MVR' && <span className={styles.minAmount}> (Minimum 100 MVR)</span>}
        </label>
        <input
          type="number"
          className={styles.input}
          placeholder={`Enter amount in ${selectedCoin}`}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </div>

      {/* Gift Card Code Redeem Section */}
      <div className={styles.giftCardSection}>
        <div className={styles.giftCardHeader}>
          <img
            src="/assets/images/bonus/2.png"
            alt="Gift Card"
            className={styles.giftIcon}
            loading="lazy"
          />
          <label className={styles.label}>Redeem Gift Card Code (Optional)</label>
        </div>
        <div className={styles.giftCardInputWrapper}>
          <Popover.Root 
            open={isGiftCardPopoverOpen && (giftCardCode.trim().length >= 3 || isSearchingGiftCard)} 
            onOpenChange={(open) => {
              // Only close if not searching and query is less than 3 chars
              if (!open && !isSearchingGiftCard && giftCardCode.trim().length < 3) {
                setIsGiftCardPopoverOpen(false);
              }
            }}
          >
            <Popover.Anchor asChild>
              <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                <input
                  type="text"
                  className={`${styles.input} ${giftCardValid ? styles.inputValid : ''} ${giftCardError ? styles.inputError : ''}`}
                  placeholder="Enter gift card code"
                  value={giftCardCode}
                  onChange={handleGiftCardCodeChange}
                  maxLength={20}
                />
              </div>
            </Popover.Anchor>
          <Popover.Portal>
            <Popover.Content 
              className={styles.giftCardPopoverContent} 
              sideOffset={8}
              align="start"
              style={{ outline: 'none', border: 'none', zIndex: 10000 }}
            >
              {isSearchingGiftCard ? (
                <div className={styles.giftCardSearchLoading}>
                  <Loader2 className={styles.spinner} size={20} />
                  <span>Searching...</span>
                </div>
              ) : giftCardSearchResults ? (
                <div className={styles.giftCardSearchResult}>
                  <div 
                    className={styles.giftCardSearchResultItem}
                    onClick={handleViewGiftCardDetails}
                  >
                    <div className={styles.giftCardSearchResultLeft}>
                      <span className={styles.giftCardSearchResultName}>
                        {giftCardSearchResults.title || giftCardSearchResults.activationCode}
                      </span>
                    </div>
                    <button
                      className={styles.giftCardSearchResultInfoButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewGiftCardDetails();
                      }}
                      aria-label="View gift card details"
                    >
                      <Info size={16} />
                    </button>
                  </div>
                </div>
              ) : giftCardCode.trim().length >= 3 ? (
                <div className={styles.giftCardSearchNoResults}>
                  No gift card found with code "{giftCardCode}"
                </div>
              ) : giftCardCode.trim().length > 0 && giftCardCode.trim().length < 3 ? (
                <div className={styles.giftCardSearchNoResults}>
                  Please enter at least 3 characters to search
                </div>
              ) : null}
            </Popover.Content>
          </Popover.Portal>
          </Popover.Root>
        </div>
        {giftCardError && (
          <p className={styles.errorText}>{giftCardError}</p>
        )}
        {giftCardValid && giftCardDetails && (
          <div className={styles.giftCardSuccess}>
            <CheckCircle size={16} />
            <span>Gift card found. {giftCardDetails.requiresDeposit ? 'Will be activated after deposit.' : 'Click on the gift card above to view details and activate.'}</span>
          </div>
        )}
      </div>

      {/* Payment Mode Selection (for INR) */}
      {selectedCoin === 'INR' && (
        <div className={styles.paymentModeContainer}>
          <div className={styles.methodSelector}>
            <button
              className={`${styles.methodButton} ${paymentMode === 'automatic' ? styles.methodButtonActive : ''}`}
              onClick={() => setPaymentMode('automatic')}
            >
              Automatic Payment
            </button>
            <button
              className={`${styles.methodButton} ${paymentMode === 'manual' ? styles.methodButtonActive : ''}`}
              onClick={() => setPaymentMode('manual')}
            >
              Manual Payment
            </button>
          </div>

          {paymentMode === 'automatic' && (
            <div className={styles.methodSelector}>
              <button
                className={`${styles.methodButton} ${upiMethod === 'upi1' ? styles.methodButtonActive : ''}`}
                onClick={() => setUpiMethod('upi1')}
              >
                UPI 1
              </button>
              <button
                className={`${styles.methodButton} ${upiMethod === 'upi2' ? styles.methodButtonActive : ''}`}
                onClick={() => setUpiMethod('upi2')}
              >
                Ok Pay
              </button>
            </div>
          )}

          {paymentMode === 'automatic' && upiMethod === 'upi2' && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Phone Number</label>
              <input
                type="tel"
                className={styles.input}
                placeholder="Enter phone number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </div>
          )}
        </div>
      )}

      {/* Transaction Form (for manual deposits) */}
      {showTransactionForm && (
        <div className={styles.transactionForm}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Transaction/UTR Number</label>
            <input
              type="text"
              className={styles.input}
              placeholder="Enter transaction ID"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Payment Screenshot</label>
            <div
              className={styles.fileUpload}
              onClick={() => document.getElementById('screenshotInput').click()}
            >
              <Upload size={24} />
              <span>{screenshot ? screenshot.name : 'Upload Payment Screenshot'}</span>
            </div>
            <input
              id="screenshotInput"
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>

          <Button
            variant="primary"
            fullWidth
            onClick={handleManualDepositSubmit}
            disabled={manualDepositMutation.isPending}
          >
            {manualDepositMutation.isPending ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      )}

      {/* Action Buttons */}
      {!showTransactionForm && (
        <div className={styles.actionButtons}>
          {isFiatCurrency(selectedCoin) ? (
            selectedCoin === 'INR' && paymentMode === 'automatic' ? (
              <Button
                variant="primary"
                fullWidth
                onClick={handleINRAutomaticPayment}
                disabled={isLoading || !amount}
              >
                {isLoading ? 'Processing...' : 'Proceed to Payment'}
              </Button>
            ) : (
              <Button
                variant="primary"
                fullWidth
                onClick={() => setShowTransactionForm(true)}
                disabled={!amount || !selectedBank}
              >
                Proceed to Payment
              </Button>
            )
          ) : (
            !showDepositDetails && (
              <Button
                variant="primary"
                fullWidth
                onClick={handleCryptoDeposit}
                disabled={isLoading || !amount || !selectedNetwork}
              >
                {isLoading ? 'Generating Address...' : 'Generate Deposit Address'}
              </Button>
            )
          )}
        </div>
      )}

      {/* Warning & Info */}
      <div className={styles.warningContainer}>
        <Info size={20} className={styles.warningIcon} />
        <p className={styles.warningText}>
          Do not deposit any currency other than {selectedCoin}
        </p>
      </div>

      <div className={styles.noteContainer}>
        <Info size={20} className={styles.noteIcon} />
        <p className={styles.noteText}>
          Usually deposits are instant but in some cases {selectedCoin} deposits can take up to 2hrs.
          If you haven't received your deposit amount, please contact customer support.
        </p>
      </div>

      {/* Payment Iframe Modal */}
      {showIframe && (
        <div className={styles.iframeOverlay}>
          <div className={styles.iframeContainer}>
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowIframe(false);
                setIframeUrl('');
                window.location.reload();
              }}
            >
              <X size={20} />
            </button>
            <iframe
              src={iframeUrl}
              className={styles.iframe}
              title="Payment"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
          </div>
        </div>
      )}

      {/* Gift Card View Modal */}
      {selectedGiftCard && (
        <GiftCardViewModal
          isOpen={showGiftCardModal}
          onClose={() => {
            setShowGiftCardModal(false);
            setSelectedGiftCard(null);
          }}
          card={selectedGiftCard}
          onActivate={handleGiftCardActivate}
        />
      )}
    </div>
  );
};

export default DepositTab;
